import { useSelector } from "react-redux";
import Modal from "../../../../components/elements/Modal";
import dayjs from "dayjs";
import { t } from "i18next";
import { lngDetect } from "../../../../helpers/lngDetect";

const EnquiryDetailsModal = ({ isOpen, onClose, data }) => {
  const { lang } = useSelector((state) => state?.langProvider);

  if (!data) return null;

  const formatDate = (dateString) => {
    return dateString ? dayjs(dateString).format("YYYY-MM-DD hh:mm A") : "N/A";
  };

  const Field = ({ label, value, type = "text", span = false }) => (
    <div className={`${span ? "md:col-span-2" : ""}`}>
      <label className="block text-sm font-semibold text-gray-700 ml-3 mb-2">
        {label}
      </label>
      <div
        className={`p-4 bg-gray-50 rounded-lg border border-gray-100 ${
          type === "textarea" ? "min-h-[80px]" : ""
        }`}
      >
        <p
          className={`text-gray-800 leading-relaxed font-medium ${
            type === "textarea" ? "whitespace-pre-wrap" : ""
          }`}
        >
          {value || "No data provided"}
        </p>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose}
      title={t("Enquiry Details")}
      size="xl2"
      titleIcon="fa-regular fa-envelope"
      titleClass="!font-semibold !text-xl !text-deep-purple"
      headerClass="!bg-gray-50 border-b border-gray-200"
      childrenClasses="!bg-white"
      bodyClasses="!p-8"
    >
      <div className="space-y-8">
        {/* Enhanced Header Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 shadow-sm">
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="fa-regular fa-envelope text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-blue-800">
                    {t("Enquiry")} #{data.uid}
                  </h3>
                  <p className="text-sm text-blue-600">ID: {data._id}</p>
                </div>
              </div>
              {data.organizationDetail?.name && (
                <div className="ml-13">
                  <p className="text-sm font-medium text-blue-700">
                    <i className="fa-regular fa-building mr-2"></i>
                    {lngDetect(data.organizationDetail.name)}
                  </p>
                </div>
              )}
            </div>
            <div className="text-right space-y-2">
              <div className="text-sm text-blue-600">
                <i className="fa-regular fa-clock mr-1"></i>
                {formatDate(data.createdAt)}
              </div>
              <div className="flex justify-end">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    data.responded
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full mr-2 ${
                      data.responded ? "bg-green-400" : "bg-yellow-400"
                    }`}
                  ></span>
                  {data.responded ? "Responded" : "Pending Response"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        {(data.name || data.email) && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 border-b border-gray-200 pb-2">
              <i className="fa-regular fa-user text-gray-600"></i>
              <h4 className="text-lg font-semibold text-gray-800">
                {t("Contact Information")}
              </h4>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {data.name && <Field label={t("Full Name")} value={data.name} />}
              {data.email && (
                <Field label={t("Email Address")} value={data.email} />
              )}
            </div>
          </div>
        )}

        {/* Message Section */}
        {data.message && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 border-b border-gray-200 pb-2">
              <i className="fa-regular fa-comment-dots text-gray-600"></i>
              <h4 className="text-lg font-semibold text-gray-800">
                {t("Message")}
              </h4>
            </div>
            <div className="grid grid-cols-1">
              <Field
                label={t("Enquiry Message")}
                value={data.message}
                type="textarea"
                span={true}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EnquiryDetailsModal;
